/**
 * 15 telas adicionais em um arquivo. Padrão CRUD/lista.
 * Componentes exportados casam com os slugs do MODULES_CATALOG.
 */
import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react';
import { useAuth } from '../lib/auth-context.js';
import { api, type Operadora } from '../lib/api.js';
import { useLiveEvent, type LiveEventPayload } from '../lib/useLiveEvents.js';
import { LiveIndicator } from '../components/LiveIndicator.js';
import { EmptyState } from '../components/EmptyState.js';
import { DownloadButton } from '../components/DownloadButton.js';
import { SessionDetailModal } from '../components/SessionDetailModal.js';

// ---------- helpers ----------
async function jsonFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const r = await fetch(path, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...(init?.headers ?? {}) },
    ...init,
  });
  if (!r.ok) throw new Error(String(r.status));
  return (await r.json()) as T;
}

function useApi<T>(path: string, deps: unknown[] = [], intervalMs = 0) {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const load = useCallback(() => {
    jsonFetch<T>(path)
      .then((d) => {
        setData(d);
        setError(null);
      })
      .catch((e: Error) => setError(e.message));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  useEffect(() => {
    load();
    if (!intervalMs) return;
    const t = setInterval(load, intervalMs);
    return () => clearInterval(t);
  }, [load, intervalMs]);
  return { data, error, reload: load };
}

function fmtDate(iso: string | null | undefined) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('pt-BR', {
    day: '2-digit', month: '2-digit', year: '2-digit',
    hour: '2-digit', minute: '2-digit',
  });
}

function badge(text: string, kind: 'ok' | 'warn' | 'err' | 'neutral' = 'neutral') {
  const cls = {
    ok: 'nx-badge nx-badge--available',
    warn: 'nx-badge nx-badge--charging',
    err: 'nx-badge nx-badge--fault',
    neutral: 'nx-badge nx-badge--neutral',
  }[kind];
  return <span className={cls}>{text}</span>;
}

// ---------- CSV import modal (reused by Fleet + Drivers) ----------
interface ImportSummary {
  total: number;
  inserted: number;
  updated: number;
  skipped: number;
  errors: Array<{ line: number; message: string; raw: string }>;
}
interface CsvImportModalProps {
  title: string;
  columns: string[]; // header esperado, exibido na prévia
  postUrl: string;
  templateUrl: string;
  templateFilename: string;
  onClose: () => void;
  onDone: () => void;
}

/**
 * Parser de CSV mínimo para PRÉVIA no browser. Não é production-grade: aceita
 * separador vírgula ou ponto-e-vírgula e aspas duplas simples. O parse real
 * (upsert no banco) acontece no servidor com csv-parse.
 */
function parsePreviewCsv(text: string): { headers: string[]; rows: string[][] } {
  const clean = text.replace(/^﻿/, '');
  const lines = clean.split(/\r?\n/).filter((l) => l.length > 0);
  if (!lines.length) return { headers: [], rows: [] };
  const sep = (lines[0]!.match(/;/g)?.length ?? 0) > (lines[0]!.match(/,/g)?.length ?? 0) ? ';' : ',';
  function splitLine(line: string): string[] {
    const out: string[] = [];
    let cur = '';
    let inQ = false;
    for (let i = 0; i < line.length; i++) {
      const c = line[i];
      if (c === '"') {
        if (inQ && line[i + 1] === '"') { cur += '"'; i++; }
        else inQ = !inQ;
      } else if (c === sep && !inQ) { out.push(cur); cur = ''; }
      else cur += c;
    }
    out.push(cur);
    return out.map((s) => s.trim());
  }
  const headers = splitLine(lines[0]!);
  const rows = lines.slice(1).map(splitLine);
  return { headers, rows };
}

function CsvImportModal({ title, columns, postUrl, templateUrl, templateFilename, onClose, onDone }: CsvImportModalProps) {
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<{ headers: string[]; rows: string[][] } | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [summary, setSummary] = useState<ImportSummary | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  async function readPreview() {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      setPreviewError('Arquivo maior que 5MB. Reduza ou fatie o CSV.');
      setPreview(null);
      return;
    }
    try {
      const text = await file.text();
      const p = parsePreviewCsv(text);
      if (!p.headers.length) {
        setPreviewError('CSV vazio.');
        setPreview(null);
        return;
      }
      const missing = columns.filter((c) => !p.headers.includes(c));
      if (missing.length) {
        setPreviewError(`Coluna(s) obrigatória(s) ausente(s): ${missing.join(', ')}`);
        setPreview(null);
        return;
      }
      setPreviewError(null);
      setPreview(p);
    } catch (err) {
      setPreviewError((err as Error).message || 'Erro ao ler arquivo.');
    }
  }

  async function submit() {
    if (!file) return;
    setBusy(true);
    setSubmitError(null);
    try {
      const form = new FormData();
      form.append('file', file, file.name);
      const r = await fetch(postUrl, { method: 'POST', body: form, credentials: 'include' });
      if (!r.ok) {
        const body = (await r.json().catch(() => ({}))) as { error?: string };
        if (r.status === 413) setSubmitError('Arquivo maior que 5MB.');
        else if (r.status === 400) setSubmitError(`CSV inválido: ${body.error ?? 'formato'}`);
        else setSubmitError(`Falha (${r.status}): ${body.error ?? 'erro'}`);
        return;
      }
      const s = (await r.json()) as ImportSummary;
      setSummary(s);
    } catch (err) {
      setSubmitError((err as Error).message || 'Erro de rede.');
    } finally {
      setBusy(false);
    }
  }

  const previewRows = preview?.rows.slice(0, 10) ?? [];
  const totalPreview = preview?.rows.length ?? 0;

  return (
    <div className="nx-modal" role="dialog" aria-modal="true" onClick={onClose}>
      <div className="nx-modal__card" onClick={(e) => e.stopPropagation()}>
        <div className="nx-modal__head">
          <h2>{title}</h2>
          <button type="button" className="nx-modal__close" onClick={onClose} aria-label="Fechar">×</button>
        </div>
        {(() => {
          const step: 1 | 2 | 3 = summary ? 3 : preview ? 2 : 1;
          const steps: Array<[1 | 2 | 3, string]> = [[1, 'Arquivo'], [2, 'Prévia'], [3, 'Resumo']];
          return (
            <div className="nx-stepper" role="progressbar" aria-valuemin={1} aria-valuemax={3} aria-valuenow={step}
              style={{ display: 'flex', gap: 8, padding: '10px 18px 0', fontSize: 'var(--nx-text-xs)' }}>
              {steps.map(([n, label]) => (
                <span key={n}
                  style={{
                    flex: 1,
                    padding: '4px 10px',
                    borderRadius: 999,
                    background: n === step ? 'var(--nx-orange-500)' : n < step ? 'var(--nx-success-500)' : 'var(--nx-neutral-200)',
                    color: n <= step ? 'var(--nx-neutral-0)' : 'var(--nx-mute)',
                    fontWeight: 600,
                    textAlign: 'center',
                    letterSpacing: '0.02em',
                  }}>
                  {n}. {label}
                </span>
              ))}
            </div>
          );
        })()}
        <div className="nx-modal__body">
          {!summary && (
            <>
              <p style={{ color: 'var(--nx-mute-strong)', margin: 0, fontSize: 'var(--nx-text-sm)' }}>
                Envie um CSV com o cabeçalho: <code className="mono">{columns.join(',')}</code>.
                {' '}
                <a
                  href={templateUrl}
                  download={templateFilename}
                >
                  Baixar modelo
                </a>
              </p>
              <input
                type="file"
                accept=".csv,text/csv"
                onChange={(e) => {
                  setFile(e.currentTarget.files?.[0] ?? null);
                  setPreview(null);
                  setPreviewError(null);
                }}
              />
              {file && !preview && !previewError && (
                <button type="button" className="nx-btn nx-btn--ghost-dark" onClick={() => void readPreview()}>
                  Ler prévia
                </button>
              )}
              {previewError && (
                <p style={{ color: 'var(--nx-danger-500)', margin: 0 }}>{previewError}</p>
              )}
              {preview && (
                <>
                  <p style={{ margin: 0, fontSize: 'var(--nx-text-sm)', color: 'var(--nx-mute-strong)' }}>
                    Prévia de {previewRows.length} de {totalPreview} linhas:
                  </p>
                  <div className="nx-table-wrap" style={{ maxHeight: 260, overflow: 'auto' }}>
                    <table className="nx-table">
                      <thead>
                        <tr><th>#</th>{preview.headers.map((h) => <th key={h}>{h}</th>)}</tr>
                      </thead>
                      <tbody>
                        {previewRows.map((row, i) => (
                          <tr key={i}><td className="mono">{i + 2}</td>{row.map((cell, j) => <td key={j} className="mono" style={{ fontSize: '.75rem' }}>{cell || <span style={{ color: 'var(--nx-mute)' }}>—</span>}</td>)}</tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
              {submitError && <p style={{ color: 'var(--nx-danger-500)', margin: 0 }}>{submitError}</p>}
            </>
          )}
          {summary && (
            <>
              <div className="nx-kpi-grid">
                <div className="nx-kpi nx-kpi--success">
                  <div className="nx-kpi__label">Inseridos</div>
                  <div className="nx-kpi__value mono">{summary.inserted}</div>
                </div>
                <div className="nx-kpi nx-kpi--cyan">
                  <div className="nx-kpi__label">Atualizados</div>
                  <div className="nx-kpi__value mono">{summary.updated}</div>
                </div>
                <div className="nx-kpi nx-kpi--navy">
                  <div className="nx-kpi__label">Ignorados</div>
                  <div className="nx-kpi__value mono">{summary.skipped}</div>
                </div>
              </div>
              {summary.errors.length > 0 && (
                <>
                  <p style={{ margin: 0, fontSize: 'var(--nx-text-sm)', color: 'var(--nx-mute-strong)' }}>
                    {summary.errors.length} erro(s):
                  </p>
                  <div className="nx-table-wrap" style={{ maxHeight: 260, overflow: 'auto' }}>
                    <table className="nx-table">
                      <thead><tr><th>Linha</th><th>Motivo</th><th>Conteúdo</th></tr></thead>
                      <tbody>
                        {summary.errors.map((e, i) => (
                          <tr key={i} className="nx-modal__row--error">
                            <td className="mono">{e.line}</td>
                            <td className="mono">{e.message}</td>
                            <td className="mono" style={{ fontSize: '.7rem', maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis' }}>{e.raw}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </>
          )}
        </div>
        <div className="nx-modal__foot">
          {!summary && (
            <>
              <button type="button" className="nx-btn nx-btn--ghost-dark" onClick={onClose}>Cancelar</button>
              <button
                type="button"
                className="nx-btn"
                disabled={!preview || busy}
                onClick={() => void submit()}
              >
                {busy ? 'Enviando…' : `Importar ${totalPreview} registro(s)`}
              </button>
            </>
          )}
          {summary && (
            <button type="button" className="nx-btn" onClick={() => { onDone(); onClose(); }}>
              Fechar
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------- 1. Aprovação OCPP ----------
export function ChargerApprovalPage() {
  const { data, reload } = useApi<{ pending: Array<{ id: number; cpId: string; vendor: string | null; model: string | null; createdAt: string }>}>('/api/charger-approvals', [], 5000);
  const { user } = useAuth();
  const isNoxxon = user?.operadoraId === null;
  const canLeaveWithoutOperadora = isNoxxon && user?.consorcioId === null;
  const [operadoras, setOperadoras] = useState<Operadora[]>([]);
  const [chosen, setChosen] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isNoxxon) return;
    api.operadoras().then((r) => setOperadoras(r.operadoras)).catch(() => setOperadoras([]));
  }, [isNoxxon]);

  async function act(cpId: string, action: 'approve' | 'deny') {
    setError(null);
    const operadoraId = chosen[cpId] ? Number(chosen[cpId]) : canLeaveWithoutOperadora ? null : operadoras[0]?.id ?? null;
    const res = await fetch(`/api/charger-approvals/${cpId}/${action}`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(action === 'approve' ? { operadoraId } : {}),
    });
    if (!res.ok) setError(`Falha ao ${action === 'approve' ? 'aprovar' : 'negar'} ${cpId}.`);
    reload();
  }
  return (
    <div>
      <h1>Aprovação OCPP</h1>
      <p style={{ color: 'var(--nx-mute)' }}>
        Carregadores novos ficam aqui até serem autorizados.
        {isNoxxon && ' Escolha a operadora dona do carregador ao aprovar.'}
      </p>
      {error && <div role="alert" className="nx-error" style={{ marginTop: 'var(--nx-space-3)' }}>{error}</div>}
      <div className="nx-card" style={{ marginTop: 'var(--nx-space-4)' }}>
        {!data?.pending.length ? (
          <p style={{ color: 'var(--nx-mute)', textAlign: 'center', margin: 0 }}>Nenhum pedido pendente.</p>
        ) : (
          <div className="nx-table-wrap">
            <table className="nx-table">
              <thead><tr><th>CP ID</th><th>Vendor / Modelo</th><th>Registrado em</th>{isNoxxon && <th>Operadora</th>}<th></th></tr></thead>
              <tbody>
                {data.pending.map((c) => (
                  <tr key={c.id}>
                    <td className="mono">{c.cpId}</td>
                    <td>{c.vendor ?? '—'} / {c.model ?? '—'}</td>
                    <td>{fmtDate(c.createdAt)}</td>
                    {isNoxxon && (
                      <td>
                        <select
                          className="nx-input"
                          value={chosen[c.cpId] ?? ''}
                          onChange={(e) => setChosen((m) => ({ ...m, [c.cpId]: e.target.value }))}
                          aria-label={`Operadora do carregador ${c.cpId}`}
                        >
                          {canLeaveWithoutOperadora && <option value="">Sem operadora (só Noxxon)</option>}
                          {operadoras.map((o) => (
                            <option key={o.id} value={o.id}>{o.nome}</option>
                          ))}
                        </select>
                      </td>
                    )}
                    <td>
                      <button className="nx-btn nx-btn--sm" onClick={() => void act(c.cpId, 'approve')}>Aprovar</button>{' '}
                      <button className="nx-btn nx-btn--sm nx-btn--ghost-dark" onClick={() => void act(c.cpId, 'deny')}>Negar</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ---------- 2. Painel Wall ----------
interface WallCharger {
  id: number;
  cpId: string;
  vendor: string | null;
  model: string | null;
  connected: boolean;
  lastHeartbeatAt: string | null;
  connectors: Array<{ connectorId: number; status: string; errorCode: string | null; updatedAt: string }>;
  activeTx: number;
}
function connectorTone(status: string): string {
  if (status === 'Charging') return 'nx-badge--charging';
  if (status === 'Available') return 'nx-badge--available';
  if (status === 'Preparing' || status === 'Finishing') return 'nx-badge--neutral';
  if (status === 'Faulted' || status === 'Unavailable') return 'nx-badge--fault';
  return 'nx-badge--neutral';
}
export function ChargingWallPage() {
  const { data } = useApi<{ chargers: WallCharger[] }>('/api/wall', [], 3000);
  const list = data?.chargers ?? [];
  const online = list.filter((c) => c.connected).length;
  const charging = list.filter((c) => c.activeTx > 0).length;
  return (
    <div>
      <div className="nx-page-head">
        <div className="nx-page-head__meta">
          <span className="nx-page-head__eyebrow">Operação</span>
          <h1>Painel Wall</h1>
          <p className="nx-page-head__sub">
            {list.length} carregador{list.length === 1 ? '' : 'es'} · {online} online · {charging} carregando
          </p>
        </div>
        <div className="nx-page-head__actions">
          <span className="nx-live-badge nx-live-badge--on"><span className="nx-live-dot" /> ao vivo</span>
        </div>
      </div>

      {list.length === 0 && (
        <div className="nx-card" style={{ textAlign: 'center', color: 'var(--nx-mute)', padding: '2rem' }}>
          Nenhum carregador aprovado ainda. Aprove em <b>Aprovação OCPP</b>.
        </div>
      )}

      <div className="nx-kpi-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '14px' }}>
        {list.map((c) => {
          // Padrão STALE: quando o CP está offline (WS caiu), o número de
          // sessões ativas no banco NÃO reflete a realidade — pode ser um
          // valor herdado de antes do WS morrer. Marcamos o card como stale
          // e degradamos o valor pra "offline" ao invés de "N sessões".
          const stale = !c.connected;
          const tone = stale ? 'navy' : c.activeTx > 0 ? 'orange' : 'success';
          const bigLabel = stale
            ? 'offline'
            : c.activeTx > 0
              ? `${c.activeTx} sess${c.activeTx > 1 ? 'ões' : 'ão'}`
              : 'livre';
          const staleStyle: React.CSSProperties = stale
            ? { opacity: 0.7, filter: 'grayscale(0.5)' }
            : {};
          return (
            <div
              key={c.id}
              className={`nx-kpi nx-kpi--${tone}`}
              style={{ display: 'flex', flexDirection: 'column', gap: 10, ...staleStyle }}
              title={stale ? 'Carregador offline — os valores mostrados são o último snapshot recebido antes da queda do link OCPP' : undefined}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div className="mono" style={{ fontSize: '.9rem', fontWeight: 700 }}>{c.cpId}</div>
                {c.connected
                  ? <span className="nx-badge nx-badge--available">● online</span>
                  : <span className="nx-badge nx-badge--fault">● offline</span>}
              </div>
              <div style={{ fontSize: '1.5rem', fontWeight: 800, letterSpacing: '-0.02em', color: 'var(--nx-ink)' }}>
                {stale
                  ? <span style={{ color: 'var(--nx-mute)' }}>{bigLabel}</span>
                  : c.activeTx > 0
                    ? <span style={{ color: 'var(--nx-orange-500)' }}>⚡ {bigLabel}</span>
                    : bigLabel}
              </div>
              <div style={{ fontSize: '.7rem', color: 'var(--nx-mute)' }}>
                {[c.vendor, c.model].filter(Boolean).join(' · ') || '—'}
              </div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {/* connectorId=0 é o status da station/EVSE (agregado OCPP),
                    não conector físico. Físicos começam em 1. */}
                {(() => {
                  const physical = c.connectors.filter((k) => k.connectorId > 0);
                  if (physical.length === 0) {
                    return <span style={{ color: 'var(--nx-mute)', fontSize: '.75rem' }}>sem conectores reportados</span>;
                  }
                  return physical.map((k) => (
                    <span
                      key={k.connectorId}
                      className={`nx-badge ${stale ? 'nx-badge--neutral' : connectorTone(k.status)}`}
                      title={stale ? `Último status conhecido: ${k.status}` : (k.errorCode ?? '')}
                    >
                      #{k.connectorId} {k.status}{stale ? ' (último)' : ''}
                    </span>
                  ));
                })()}
              </div>
              <div style={{ fontSize: '.7rem', color: stale ? 'var(--nx-warning)' : 'var(--nx-mute)' }}>
                {stale ? '⚠ Sem link OCPP — ' : 'Último heartbeat: '}
                {c.lastHeartbeatAt ? fmtDate(c.lastHeartbeatAt) : '—'}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ---------- 3. Saúde da Bateria ----------
// Implementação atual: apps/web/src/pages/SaudeBateriaPage.tsx. A versão
// tabelada antiga (`BatteryHealthPage()` que consumia /api/battery-health)
// foi removida por estar órfã — não era roteada em AppShell nem importada
// em nenhum lugar, e ainda usava o antipadrão de exibir SoH sem estado
// vazio honesto (BUG-QA2-5).

// ---------- 4. Manutenção ----------
interface MOrder { id: number; cpId: string; kind: string; status: string; priority: string; title: string; description: string | null; scheduledFor: string | null; technician: string | null; completedAt: string | null; createdAt: string; }
export function MaintenancePage() {
  const [statusFilter, setStatus] = useState('open');
  const { data, reload } = useApi<{ orders: MOrder[] }>(`/api/maintenance?status=${statusFilter}`, [statusFilter], 10000);
  const [showForm, setShowForm] = useState(false);
  const { data: chargers } = useApi<{ chargers: Array<{ id: number; cpId: string }> }>('/api/chargers');

  async function create(e: FormEvent) {
    e.preventDefault();
    const fd = new FormData(e.currentTarget as HTMLFormElement);
    const body = {
      chargerId: Number(fd.get('chargerId')),
      kind: fd.get('kind') as string,
      priority: fd.get('priority') as string,
      title: fd.get('title') as string,
      description: fd.get('description') as string,
      technician: fd.get('technician') as string || undefined,
      scheduledFor: fd.get('scheduledFor') ? new Date(String(fd.get('scheduledFor'))).toISOString() : undefined,
    };
    await jsonFetch('/api/maintenance', { method: 'POST', body: JSON.stringify(body) });
    setShowForm(false);
    reload();
  }
  async function close(id: number) {
    if (!confirm('Fechar esta ordem?')) return;
    await fetch(`/api/maintenance/${id}/close`, { method: 'POST', credentials: 'include' });
    reload();
  }
  return (
    <div>
      <div className="nx-dash__head"><h1>Manutenção</h1><button className="nx-btn" onClick={() => setShowForm((s) => !s)}>{showForm ? 'Cancelar' : '+ Nova ordem'}</button></div>
      <div style={{ marginBottom: 'var(--nx-space-4)' }}>
        {['open', 'in_progress', 'done', ''].map((s) => (
          <button key={s || 'all'} type="button" className={`nx-btn nx-btn--sm ${statusFilter === s ? '' : 'nx-btn--ghost-dark'}`} onClick={() => setStatus(s)} style={{ marginRight: 8 }}>
            {s === '' ? 'Todas' : s === 'open' ? 'Abertas' : s === 'in_progress' ? 'Em andamento' : 'Concluídas'}
          </button>
        ))}
      </div>
      {showForm && (
        <div className="nx-card" style={{ marginBottom: 'var(--nx-space-4)' }}>
          <form onSubmit={create}>
            <div className="nx-filters">
              <label className="nx-field"><span>Carregador</span>
                <select name="chargerId" required>{(chargers?.chargers ?? []).map((c) => <option key={c.id} value={c.id}>{c.cpId}</option>)}</select>
              </label>
              <label className="nx-field"><span>Tipo</span>
                <select name="kind"><option value="corretiva">Corretiva</option><option value="preventiva">Preventiva</option><option value="inspecao">Inspeção</option></select>
              </label>
              <label className="nx-field"><span>Prioridade</span>
                <select name="priority" defaultValue="normal"><option value="low">Baixa</option><option value="normal">Normal</option><option value="high">Alta</option><option value="urgent">Urgente</option></select>
              </label>
              <label className="nx-field"><span>Título</span><input name="title" required /></label>
              <label className="nx-field"><span>Técnico responsável</span><input name="technician" placeholder="Nome ou equipe" /></label>
              <label className="nx-field"><span>Data prevista</span><input name="scheduledFor" type="datetime-local" /></label>
            </div>
            <label className="nx-field" style={{ marginTop: 'var(--nx-space-3)' }}><span>Descrição</span><textarea name="description" rows={3} style={{ width: '100%', padding: 'var(--nx-space-3)', border: '1px solid var(--nx-line)', borderRadius: 'var(--nx-radius-sm)', background: 'var(--nx-bg)', color: 'var(--nx-ink)' }} /></label>
            <button className="nx-btn" type="submit" style={{ marginTop: 'var(--nx-space-3)' }}>Criar</button>
          </form>
        </div>
      )}
      <div className="nx-card"><div className="nx-table-wrap">
        <table className="nx-table">
          <thead><tr><th>Charger</th><th>Tipo</th><th>Prior.</th><th>Título</th><th>Técnico</th><th>Status</th><th>Agendada</th><th></th></tr></thead>
          <tbody>
            {(data?.orders ?? []).map((o) => (
              <tr key={o.id}>
                <td className="mono">{o.cpId}</td>
                <td>{o.kind}</td>
                <td>{badge(o.priority, o.priority === 'urgent' || o.priority === 'high' ? 'err' : 'neutral')}</td>
                <td>{o.title}</td>
                <td>{o.technician ?? '—'}</td>
                <td>{badge(o.status, o.status === 'done' ? 'ok' : 'warn')}</td>
                <td>{fmtDate(o.scheduledFor)}</td>
                <td>{o.status !== 'done' && <button className="nx-btn nx-btn--sm" onClick={() => void close(o.id)}>Fechar</button>}</td>
              </tr>
            ))}
            {!data?.orders.length && <tr><td colSpan={8} style={{ color: 'var(--nx-mute)', textAlign: 'center' }}>Nenhuma ordem.</td></tr>}
          </tbody>
        </table>
      </div></div>
    </div>
  );
}

// ---------- 5. Monitor ao Vivo ----------
export function LivePage() {
  interface LC { id: number; cpId: string; connected: boolean; connectors: Array<{ connectorId: number; status: string }>; activeTransactions: Array<{ ocppTransactionId: number; idTag: string; chassiVin: string | null; startTime: string; energyWh: number | null; powerW: number | null; meterAt: string | null }>; }
  // Fallback: refetch a cada 30s. Push via /ws/live cobre o intervalo.
  const { data, reload } = useApi<{ chargers: LC[] }>('/api/live', [], 30_000);
  const [events, setEvents] = useState<Array<{ ts: string; text: string }>>([]);

  const pushEvent = useCallback((ts: string, text: string) => {
    setEvents((prev) => [{ ts, text }, ...prev].slice(0, 100));
  }, []);

  useLiveEvent('transaction.started', (p: LiveEventPayload<'transaction.started'>, ts) => {
    pushEvent(ts, `transaction.started · ${p.cpId} #${p.connectorId} tx${p.txId} idTag=${p.idTag}`);
    void reload();
  });
  useLiveEvent('transaction.ended', (p: LiveEventPayload<'transaction.ended'>, ts) => {
    pushEvent(ts, `transaction.ended · ${p.cpId} tx${p.txId} ${(p.energyWh / 1000).toFixed(2)}kWh reason=${p.stopReason ?? '—'}`);
    void reload();
  });
  useLiveEvent('transaction.metervalue', (p: LiveEventPayload<'transaction.metervalue'>, ts) => {
    pushEvent(ts, `metervalue · ${p.cpId} tx${p.txId} ${(p.powerW / 1000).toFixed(2)}kW ${(p.energyWh / 1000).toFixed(3)}kWh${p.soc != null ? ` SoC=${p.soc}%` : ''}`);
  });
  useLiveEvent('charger.status', (p: LiveEventPayload<'charger.status'>, ts) => {
    pushEvent(ts, `charger.status · ${p.cpId} #${p.connectorId} → ${p.status}`);
    void reload();
  });

  return (
    <div>
      <div className="nx-dash__head"><h1>Monitor ao Vivo</h1><LiveIndicator /></div>
      <div className="nx-kpi-grid">
        {(data?.chargers ?? []).map((c) => {
          // Padrão STALE: CP offline → mesmo que activeTransactions[] esteja
          // populado no banco (última leitura antes do WS cair), NÃO mostrar
          // "⚡ carregando". Degradar para "offline" e rotular tx como último
          // conhecido pra o operador não confiar em número congelado.
          const stale = !c.connected;
          const tone = stale ? 'navy' : c.activeTransactions.length ? 'orange' : 'success';
          const staleStyle: React.CSSProperties = stale
            ? { opacity: 0.7, filter: 'grayscale(0.5)' }
            : {};
          return (
            <div
              key={c.id}
              className={`nx-kpi nx-kpi--${tone}`}
              style={staleStyle}
              title={stale ? 'Carregador offline — dados mostrados são o último snapshot antes da queda do link OCPP' : undefined}
            >
              <div className="nx-kpi__label mono">{c.cpId}</div>
              <div className="nx-kpi__value" style={{ fontSize: '1.2rem' }}>
                {stale ? 'offline' : c.activeTransactions.length ? '⚡ carregando' : 'livre'}
              </div>
              <div className="nx-kpi__hint">
                {c.connectors.filter((k) => k.connectorId > 0).map((k) => `#${k.connectorId} ${k.status}${stale ? ' (último)' : ''}`).join(' • ') || '—'}
                {c.activeTransactions.map((t) => (
                  <div key={t.ocppTransactionId} className="mono" style={{ fontSize: '.75rem', marginTop: 4, color: stale ? 'var(--nx-warning)' : undefined }}>
                    tx #{t.ocppTransactionId} · {t.chassiVin ?? `RFID ${t.idTag}`} · {Math.max(0, Math.floor((Date.now() - new Date(t.startTime).getTime()) / 60_000))} min · {t.energyWh == null ? 'energia aguardando' : `${(t.energyWh / 1000).toFixed(2)} kWh`} · {t.powerW == null ? 'potência aguardando' : `${(t.powerW / 1000).toFixed(1)} kW`}{stale ? ' (último)' : ''}
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
      <div className="nx-card" style={{ marginTop: 'var(--nx-space-6)' }}>
        <h2 style={{ fontSize: '1rem' }}>Eventos ao vivo (últimos 100)</h2>
        {!events.length && <p style={{ color: 'var(--nx-mute)' }}>Aguardando eventos…</p>}
        <ul className="mono" style={{ margin: 0, padding: 0, listStyle: 'none', fontSize: '.75rem', lineHeight: 1.6 }}>
          {events.map((e, i) => (<li key={i}><span style={{ color: 'var(--nx-mute)' }}>{new Date(e.ts).toLocaleTimeString('pt-BR')}</span> — {e.text}</li>))}
        </ul>
      </div>
    </div>
  );
}

// ---------- 6. Fases de Carga ----------
interface Phase { id: number; ocppTransactionId: number; cpId: string; startTime: string; stopTime: string | null; energyWh: number | null; samples: number; avgPowerW: number; finalSoc: number; }
export function ChargePhasesPage() {
  const { data } = useApi<{ transactions: Phase[] }>('/api/charge-phases', [], 15000);
  const [selectedSessionId, setSelectedSessionId] = useState<number | null>(null);
  return (
    <div>
      <h1>Fases de Carga</h1>
      <p style={{ color: 'var(--nx-mute)' }}>Última centena de transações com estatísticas agregadas das leituras.</p>
      <div className="nx-card"><div className="nx-table-wrap">
        <table className="nx-table">
          <thead><tr><th>#</th><th>Charger</th><th>Início</th><th>Fim</th><th>Amostras</th><th>Pot. média</th><th>Energia</th><th>SoC final</th></tr></thead>
          <tbody>
            {(data?.transactions ?? []).map((t) => (
              <tr key={t.ocppTransactionId} onClick={() => setSelectedSessionId(t.id)} style={{ cursor: 'pointer' }} title="Abrir análise completa da sessão">
                <td className="mono">#{t.ocppTransactionId}</td>
                <td className="mono">{t.cpId}</td>
                <td>{fmtDate(t.startTime)}</td>
                <td>{t.stopTime ? fmtDate(t.stopTime) : <em>em curso</em>}</td>
                <td className="mono">{t.samples}</td>
                <td className="mono">{(t.avgPowerW / 1000).toFixed(2)} kW</td>
                <td className="mono">{t.energyWh == null ? '—' : (t.energyWh / 1000).toFixed(3) + ' kWh'}</td>
                <td>{t.finalSoc ? badge(`${t.finalSoc}%`, t.finalSoc >= 80 ? 'ok' : 'neutral') : '—'}</td>
              </tr>
            ))}
            {!data?.transactions.length && <tr><td colSpan={8} style={{ color: 'var(--nx-mute)', textAlign: 'center' }}>Sem transações.</td></tr>}
          </tbody>
        </table>
      </div></div>
      {selectedSessionId !== null && <SessionDetailModal sessionId={selectedSessionId} onClose={() => setSelectedSessionId(null)} />}
    </div>
  );
}

// ---------- 7. Logs OCPP ----------
export function OcppLogsPage() {
  const [cpFilter, setCpFilter] = useState('');
  const { data } = useApi<{ messages: Array<{ id: number; cpId: string; direction: string; action: string; uid: string; payload: string; createdAt: string }> }>(`/api/ocpp-logs${cpFilter ? '?cpId=' + encodeURIComponent(cpFilter) : ''}`, [cpFilter], 10000);
  return (
    <div>
      <h1>Logs OCPP</h1>
      <div className="nx-card" style={{ marginBottom: 'var(--nx-space-4)' }}>
        <label className="nx-field" style={{ maxWidth: 300 }}><span>Filtrar por CP ID</span><input value={cpFilter} onChange={(e) => setCpFilter(e.target.value)} placeholder="ex: NX-SIM-01" /></label>
      </div>
      <div className="nx-card"><div className="nx-table-wrap">
        <table className="nx-table">
          <thead><tr><th>Quando</th><th>Charger</th><th>Direção</th><th>Ação</th><th>UID</th><th>Payload</th></tr></thead>
          <tbody>
            {(data?.messages ?? []).map((m) => (
              <tr key={m.id}>
                <td className="mono" style={{ fontSize: '.75rem' }}>{fmtDate(m.createdAt)}</td>
                <td className="mono">{m.cpId}</td>
                <td>{badge(m.direction === 'in' ? '← in' : 'out →', m.direction === 'in' ? 'neutral' : 'ok')}</td>
                <td className="mono">{m.action}</td>
                <td className="mono" style={{ fontSize: '.7rem', color: 'var(--nx-mute)' }}>{m.uid.slice(0, 8)}</td>
                <td className="mono" style={{ fontSize: '.7rem', maxWidth: 400, overflow: 'hidden', textOverflow: 'ellipsis' }}>{m.payload}</td>
              </tr>
            ))}
            {!data?.messages.length && <tr><td colSpan={6} style={{ color: 'var(--nx-mute)', textAlign: 'center' }}>Sem mensagens.</td></tr>}
          </tbody>
        </table>
      </div></div>
    </div>
  );
}

// ---------- 8. Telemetria Sub. ----------
// FASE 16.3 — a tabela `substation_readings` não tem writer e o medidor físico
// do PoE ainda não foi instalado. Em vez de mostrar tabela vazia (que parecia
// bug), mostramos um estado explícito de "aguardando integração". Quando o
// hardware chegar, restaurar consumo de /api/substation/meters aqui.
export function TelemetriaSubPage() {
  const { user } = useAuth();
  const { data, error, reload } = useApi<{
    source: 'real' | 'simulated' | 'unavailable';
    simulationEnabled: boolean;
    readings: Array<{ ts: string; voltageV: number | null; currentA: number | null; powerKw: number | null; energyKwh: number | null; powerFactor: number | null }>;
    configuration: { chargerPowerKw: number; vehicleCapacityKwh: number; activeChargers: number };
  }>('/api/substation/telemetry', [], 10_000);
  const [toggling, setToggling] = useState(false);

  async function setSimulation(enabled: boolean) {
    setToggling(true);
    try {
      await jsonFetch('/api/substation/simulation', { method: 'PATCH', body: JSON.stringify({ enabled }) });
      reload();
    } finally {
      setToggling(false);
    }
  }

  const latest = data?.readings.at(-1) ?? null;
  const sourceLabel = data?.source === 'real' ? 'Leitura real' : data?.source === 'simulated' ? 'Simulação ativa' : 'Sem leitura';
  return (
    <div>
      <div className="nx-page-head">
        <div className="nx-page-head__title">
          <span className="nx-page-head__eyebrow">Subestação</span>
          <h1>Telemetria Subestação</h1>
          <span className="nx-page-head__sub">Potência ativa/reativa, demanda contratada e consumo do transformador</span>
        </div>
        {user?.role === 'admin' && data?.simulationEnabled && (
          <button className="nx-btn nx-btn--ghost-dark" onClick={() => void setSimulation(false)} disabled={toggling}>
            {toggling ? 'Desligando…' : 'Desligar simulação'}
          </button>
        )}
        {user?.role === 'admin' && data && !data.simulationEnabled && (
          <button className="nx-btn" onClick={() => void setSimulation(true)} disabled={toggling}>
            {toggling ? 'Ativando…' : 'Ativar simulação'}
          </button>
        )}
      </div>
      {error && <div className="nx-error">Não foi possível carregar a telemetria: {error}</div>}
      {data?.source === 'unavailable' && (
        <EmptyState
          title="Aguardando integração com medidor da subestação"
          description="Ainda não há leitura externa. Um administrador pode ativar a simulação identificada para validar a tela sem contaminar os dados reais."
          hint="A simulação usa os parâmetros configurados em Ajustes e não grava leituras no banco."
        />
      )}
      {data && data.source !== 'unavailable' && (
        <>
          <div className="nx-kpi-grid" style={{ marginTop: 'var(--nx-space-4)' }}>
            <div className="nx-kpi-card"><span className="nx-kpi-card__label">Fonte</span><strong>{sourceLabel}</strong><small>{data.source === 'simulated' ? 'Dados demonstrativos, não medidos' : 'Coletor da subestação'}</small></div>
            <div className="nx-kpi-card"><span className="nx-kpi-card__label">Potência atual</span><strong>{latest?.powerKw ?? '—'} kW</strong><small>Última leitura</small></div>
            <div className="nx-kpi-card"><span className="nx-kpi-card__label">Tensão</span><strong>{latest?.voltageV ?? '—'} V</strong><small>Trifásico</small></div>
            <div className="nx-kpi-card"><span className="nx-kpi-card__label">Corrente</span><strong>{latest?.currentA ?? '—'} A</strong><small>Fator de potência {latest?.powerFactor?.toFixed(2) ?? '—'}</small></div>
          </div>
          <div className="nx-card" style={{ marginTop: 'var(--nx-space-4)' }}>
            <div className="nx-dash__head"><div><h2 style={{ margin: 0, fontSize: '1rem' }}>Últimas leituras</h2><p style={{ margin: '4px 0 0', color: 'var(--nx-mute)' }}>Parâmetros: {data.configuration.activeChargers} carregador(es) de {data.configuration.chargerPowerKw} kW · bateria de referência {data.configuration.vehicleCapacityKwh} kWh</p></div>{badge(sourceLabel, data.source === 'real' ? 'ok' : 'warn')}</div>
            <div className="nx-table-wrap"><table className="nx-table"><thead><tr><th>Data/hora</th><th>Potência</th><th>Tensão</th><th>Corrente</th><th>Energia</th></tr></thead><tbody>{data.readings.slice(-8).reverse().map((reading) => <tr key={reading.ts}><td>{fmtDate(reading.ts)}</td><td>{reading.powerKw ?? '—'} kW</td><td>{reading.voltageV ?? '—'} V</td><td>{reading.currentA ?? '—'} A</td><td>{reading.energyKwh ?? '—'} kWh</td></tr>)}</tbody></table></div>
          </div>
        </>
      )}
    </div>
  );
}

// ---------- 9. Acessos API ----------
export function ApiAccessPage() {
  const { data, reload } = useApi<{ tokens: Array<{ id: number; name: string; prefix: string; scopes: string; createdAt: string; lastUsedAt: string | null; revokedAt: string | null }> }>('/api/api-tokens');
  const [newToken, setNewToken] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  async function create(e: FormEvent) {
    e.preventDefault();
    const fd = new FormData(e.currentTarget as HTMLFormElement);
    const r = await jsonFetch<{ token: string }>('/api/api-tokens', { method: 'POST', body: JSON.stringify({ name: fd.get('name'), scopes: fd.get('scopes') }) });
    setNewToken(r.token);
    setShowForm(false);
    reload();
  }
  async function revoke(id: number) {
    if (!confirm('Revogar este token?')) return;
    await fetch(`/api/api-tokens/${id}/revoke`, { method: 'POST', credentials: 'include' });
    reload();
  }
  return (
    <div>
      <div className="nx-dash__head"><h1>Acessos API</h1><button className="nx-btn" onClick={() => setShowForm((s) => !s)}>{showForm ? 'Cancelar' : '+ Novo token'}</button></div>
      {newToken && (
        <div className="nx-card nx-tempcred">
          <strong>Token gerado (aparece 1x):</strong>
          <code className="mono nx-tempcred__code">{newToken}</code>
          <button className="nx-btn nx-btn--sm nx-btn--ghost-dark" onClick={() => setNewToken(null)} style={{ marginTop: 'var(--nx-space-3)' }}>Copiei, fechar</button>
        </div>
      )}
      {showForm && (
        <div className="nx-card" style={{ marginBottom: 'var(--nx-space-4)' }}>
          <form onSubmit={create}>
            <div className="nx-filters">
              <label className="nx-field"><span>Nome</span><input name="name" required /></label>
              <label className="nx-field"><span>Scopes (csv)</span><input name="scopes" defaultValue="read" /></label>
            </div>
            <button type="submit" className="nx-btn" style={{ marginTop: 'var(--nx-space-3)' }}>Criar</button>
          </form>
        </div>
      )}
      <div className="nx-card"><div className="nx-table-wrap">
        <table className="nx-table">
          <thead><tr><th>Nome</th><th>Prefixo</th><th>Scopes</th><th>Criado</th><th>Último uso</th><th>Status</th><th></th></tr></thead>
          <tbody>
            {(data?.tokens ?? []).map((t) => (
              <tr key={t.id}>
                <td>{t.name}</td>
                <td className="mono">{t.prefix}…</td>
                <td className="mono">{t.scopes}</td>
                <td>{fmtDate(t.createdAt)}</td>
                <td>{fmtDate(t.lastUsedAt)}</td>
                <td>{t.revokedAt ? badge('revogado', 'err') : badge('ativo', 'ok')}</td>
                <td>{!t.revokedAt && <button className="nx-btn nx-btn--sm nx-btn--ghost-dark" onClick={() => void revoke(t.id)}>Revogar</button>}</td>
              </tr>
            ))}
            {!data?.tokens.length && <tr><td colSpan={7} style={{ color: 'var(--nx-mute)', textAlign: 'center' }}>Nenhum token.</td></tr>}
          </tbody>
        </table>
      </div></div>
      <div className="nx-card" style={{ marginTop: 'var(--nx-space-6)' }}>
        <h2 style={{ fontSize: '1rem' }}>API REST para integrações</h2>
        <p style={{ color: 'var(--nx-mute)' }}>Use o token como <code className="mono">Authorization: Bearer nxv_…</code>. O token só é mostrado na criação e pode ser revogado a qualquer momento.</p>
        <div className="nx-table-wrap"><table className="nx-table"><thead><tr><th>Método</th><th>Endpoint</th><th>Dados</th></tr></thead><tbody>
          <tr><td className="mono">GET</td><td className="mono">/api/integrations/v1/chargers</td><td>Carregadores, conectores e estado atual.</td></tr>
          <tr><td className="mono">GET</td><td className="mono">/api/integrations/v1/sessions?from=&amp;to=&amp;limit=</td><td>Sessões concluídas, energia, VIN quando atribuído e motivo de parada.</td></tr>
        </tbody></table></div>
      </div>
    </div>
  );
}

// ---------- 10. Veículos (fleet) ----------
interface Vehicle { id: number; plate: string; prefix: string | null; make: string | null; model: string | null; year: number | null; capacityKwh: number | null; active: boolean; driver: { id: number; name: string } | null; }
export function FleetPage() {
  const { data, reload } = useApi<{ vehicles: Vehicle[] }>('/api/vehicles');
  const { data: drivers } = useApi<{ drivers: Array<{ id: number; name: string }> }>('/api/drivers');
  const [showForm, setShowForm] = useState(false);
  const [showImport, setShowImport] = useState(false);
  async function create(e: FormEvent) {
    e.preventDefault();
    const fd = new FormData(e.currentTarget as HTMLFormElement);
    const body: Record<string, unknown> = {
      plate: fd.get('plate'),
      prefix: fd.get('prefix') || undefined,
      make: fd.get('make') || undefined,
      model: fd.get('model') || undefined,
      year: fd.get('year') ? Number(fd.get('year')) : undefined,
      capacityKwh: fd.get('capacityKwh') ? Number(fd.get('capacityKwh')) : undefined,
      driverId: fd.get('driverId') ? Number(fd.get('driverId')) : undefined,
    };
    await jsonFetch('/api/vehicles', { method: 'POST', body: JSON.stringify(body) });
    setShowForm(false);
    reload();
  }
  return (
    <div>
      <div className="nx-dash__head">
        <h1>Veículos</h1>
        <div style={{ display: 'flex', gap: 'var(--nx-space-3)' }}>
          <button className="nx-btn nx-btn--ghost-dark" onClick={() => setShowImport(true)}>Importar CSV</button>
          <button className="nx-btn" onClick={() => setShowForm((s) => !s)}>{showForm ? 'Cancelar' : '+ Novo veículo'}</button>
        </div>
      </div>
      {showImport && (
        <CsvImportModal
          title="Importar veículos"
          columns={['plate', 'make', 'model', 'year', 'capacityKwh', 'driverId']}
          postUrl="/api/vehicles/import-csv"
          templateUrl="/api/vehicles/import-csv/template"
          templateFilename="vehicles-template.csv"
          onClose={() => setShowImport(false)}
          onDone={reload}
        />
      )}
      {showForm && (
        <div className="nx-card" style={{ marginBottom: 'var(--nx-space-4)' }}>
          <form onSubmit={create}>
            <div className="nx-filters">
              <label className="nx-field"><span>Prefixo</span><input name="prefix" placeholder="ex: 00015" /></label>
              <label className="nx-field"><span>Placa</span><input name="plate" required /></label>
              <label className="nx-field"><span>Fabricante</span><input name="make" /></label>
              <label className="nx-field"><span>Modelo</span><input name="model" /></label>
              <label className="nx-field"><span>Ano</span><input name="year" type="number" /></label>
              <label className="nx-field"><span>Capacidade (kWh)</span><input name="capacityKwh" type="number" /></label>
              <label className="nx-field"><span>Motorista</span>
                <select name="driverId" defaultValue=""><option value="">—</option>{(drivers?.drivers ?? []).map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}</select>
              </label>
            </div>
            <button type="submit" className="nx-btn" style={{ marginTop: 'var(--nx-space-3)' }}>Criar</button>
          </form>
        </div>
      )}
      <div className="nx-card"><div className="nx-table-wrap">
        <table className="nx-table">
          <thead><tr><th>Prefixo</th><th>Placa</th><th>Fabricante / Modelo</th><th>Ano</th><th>Capacidade</th><th>Motorista</th><th>Status</th></tr></thead>
          <tbody>
            {(data?.vehicles ?? []).map((v) => (
              <tr key={v.id}>
                <td className="mono" style={{ fontWeight: 700 }}>{v.prefix ?? <span style={{ color: 'var(--nx-mute)', fontWeight: 400 }}>—</span>}</td>
                <td className="mono">{v.plate}</td>
                <td>{v.make ?? '—'} / {v.model ?? '—'}</td>
                <td>{v.year ?? '—'}</td>
                <td className="mono">{v.capacityKwh ? `${v.capacityKwh} kWh` : '—'}</td>
                <td>{v.driver?.name ?? <span style={{ color: 'var(--nx-mute)' }}>—</span>}</td>
                <td>{badge(v.active ? 'ativo' : 'inativo', v.active ? 'ok' : 'err')}</td>
              </tr>
            ))}
            {!data?.vehicles.length && <tr><td colSpan={7} style={{ color: 'var(--nx-mute)', textAlign: 'center' }}>Sem veículos.</td></tr>}
          </tbody>
        </table>
      </div></div>
    </div>
  );
}

// ---------- 11. Motoristas ----------
interface Driver { id: number; name: string; document: string | null; email: string | null; phone: string | null; idTag: string | null; active: boolean; }
export function DriversPage() {
  const { data, reload } = useApi<{ drivers: Driver[] }>('/api/drivers');
  const [showForm, setShowForm] = useState(false);
  const [showImport, setShowImport] = useState(false);
  async function create(e: FormEvent) {
    e.preventDefault();
    const fd = new FormData(e.currentTarget as HTMLFormElement);
    const body: Record<string, unknown> = { name: fd.get('name') };
    for (const k of ['document', 'email', 'phone', 'idTag'] as const) {
      const v = fd.get(k);
      if (v) body[k] = v;
    }
    await jsonFetch('/api/drivers', { method: 'POST', body: JSON.stringify(body) });
    setShowForm(false);
    reload();
  }
  return (
    <div>
      <div className="nx-dash__head">
        <h1>Motoristas</h1>
        <div style={{ display: 'flex', gap: 'var(--nx-space-3)' }}>
          <button className="nx-btn nx-btn--ghost-dark" onClick={() => setShowImport(true)}>Importar CSV</button>
          <button className="nx-btn" onClick={() => setShowForm((s) => !s)}>{showForm ? 'Cancelar' : '+ Novo motorista'}</button>
        </div>
      </div>
      {showImport && (
        <CsvImportModal
          title="Importar motoristas"
          columns={['name', 'email', 'idTag', 'phone', 'document', 'status']}
          postUrl="/api/drivers/import-csv"
          templateUrl="/api/drivers/import-csv/template"
          templateFilename="drivers-template.csv"
          onClose={() => setShowImport(false)}
          onDone={reload}
        />
      )}
      {showForm && (
        <div className="nx-card" style={{ marginBottom: 'var(--nx-space-4)' }}>
          <form onSubmit={create}>
            <div className="nx-filters">
              <label className="nx-field"><span>Nome</span><input name="name" required /></label>
              <label className="nx-field"><span>Documento</span><input name="document" /></label>
              <label className="nx-field"><span>E-mail</span><input name="email" type="email" /></label>
              <label className="nx-field"><span>Telefone</span><input name="phone" /></label>
              <label className="nx-field"><span>idTag OCPP</span><input name="idTag" pattern="[A-Za-z0-9._:-]{1,20}" /></label>
            </div>
            <button type="submit" className="nx-btn" style={{ marginTop: 'var(--nx-space-3)' }}>Criar</button>
          </form>
        </div>
      )}
      <div className="nx-card"><div className="nx-table-wrap">
        <table className="nx-table">
          <thead><tr><th>Nome</th><th>Documento</th><th>E-mail</th><th>Telefone</th><th>idTag</th><th>Status</th></tr></thead>
          <tbody>
            {(data?.drivers ?? []).map((d) => (
              <tr key={d.id}>
                <td>{d.name}</td>
                <td className="mono">{d.document ?? '—'}</td>
                <td className="mono">{d.email ?? '—'}</td>
                <td className="mono">{d.phone ?? '—'}</td>
                <td className="mono">{d.idTag ?? '—'}</td>
                <td>{badge(d.active ? 'ativo' : 'inativo', d.active ? 'ok' : 'err')}</td>
              </tr>
            ))}
            {!data?.drivers.length && <tr><td colSpan={6} style={{ color: 'var(--nx-mute)', textAlign: 'center' }}>Sem motoristas.</td></tr>}
          </tbody>
        </table>
      </div></div>
    </div>
  );
}

// ---------- 12. Visão Geral Financeira ----------
export function FinancialPage() {
  const { data } = useApi<{ month: string; transactions: number; totalKwh: number; peakKwh: number; offPeakKwh: number; costBrl: number; tariff: { peakBrl: number; offPeakBrl: number; peakStart: string; peakEnd: string }}>('/api/financial/overview');
  if (!data) return <div className="nx-loader">Carregando…</div>;
  return (
    <div>
      <div className="nx-dash__head">
        <h1>Financeiro — {data.month}</h1>
        <div style={{ display: 'flex', gap: 'var(--nx-space-2)', flexWrap: 'wrap' }}>
          {/* Ambos os endpoints exigem month=YYYY-MM. `data.month` já vem
              nesse formato — servidor calcula no timezone da instalação, então
              usamos EXATAMENTE o mesmo mês exibido, sem recalcular no cliente
              (evita divergência de fuso quando meia-noite cai no meio do dia
              UTC). */}
          <DownloadButton
            href={`/api/reports/xlsx/financial?month=${encodeURIComponent(data.month)}`}
            filename={`nxvolt-financial-${data.month}.xlsx`}
            label="Exportar XLSX"
            variant="primary"
            title={`Baixa a planilha financeira de ${data.month}`}
          />
          <DownloadButton
            href={`/api/reports/pdf/financial?month=${encodeURIComponent(data.month)}`}
            filename={`nxvolt-financial-${data.month}.pdf`}
            label="Exportar PDF"
            variant="secondary"
            title={`Baixa o resumo financeiro em PDF de ${data.month}`}
          />
        </div>
      </div>
      <p style={{ color: 'var(--nx-mute)' }}>Frotista: custo total baseado nas tarifas configuráveis em Horário de Ponta.</p>
      <div className="nx-kpi-grid">
        <div className="nx-kpi nx-kpi--navy"><div className="nx-kpi__label">Transações</div><div className="nx-kpi__value mono">{data.transactions}</div></div>
        <div className="nx-kpi nx-kpi--cyan"><div className="nx-kpi__label">Energia total</div><div className="nx-kpi__value mono">{data.totalKwh.toFixed(2)} kWh</div></div>
        <div className="nx-kpi nx-kpi--orange"><div className="nx-kpi__label">Ponta</div><div className="nx-kpi__value mono">{data.peakKwh.toFixed(2)} kWh</div><div className="nx-kpi__hint">R$ {data.tariff.peakBrl.toFixed(4)}/kWh · {data.tariff.peakStart}-{data.tariff.peakEnd}</div></div>
        <div className="nx-kpi nx-kpi--success"><div className="nx-kpi__label">Fora de ponta</div><div className="nx-kpi__value mono">{data.offPeakKwh.toFixed(2)} kWh</div><div className="nx-kpi__hint">R$ {data.tariff.offPeakBrl.toFixed(4)}/kWh</div></div>
      </div>
      <div className="nx-card" style={{ marginTop: 'var(--nx-space-6)' }}>
        <div className="nx-kpi__label">Custo estimado do mês</div>
        <div style={{ fontSize: '3rem', fontWeight: 700, color: 'var(--nx-heading)' }} className="mono">R$ {data.costBrl.toFixed(2)}</div>
        <p style={{ color: 'var(--nx-mute)', fontSize: '.875rem' }}>Configure as tarifas em Configurações → financial.*</p>
      </div>
    </div>
  );
}

// ---------- 13. Plano Contratado ----------
export function PlanoContratadoPage() {
  const { data } = useApi<{ contractedDemandKw: number; peakStart: string; peakEnd: string; distributor: string | null; validFrom: string | null; validTo: string | null; peaksPerDay: Array<{ date: string; peakKw: number }> }>('/api/financial/plan');
  const maxKw = useMemo(() => Math.max(1, ...(data?.peaksPerDay ?? []).map((p) => p.peakKw)), [data]);
  return (
    <div>
      <h1>Plano Contratado</h1>
      <div className="nx-kpi-grid">
        <div className="nx-kpi nx-kpi--navy"><div className="nx-kpi__label">Demanda contratada</div><div className="nx-kpi__value mono">{data?.contractedDemandKw ?? 0} kW</div></div>
        <div className="nx-kpi nx-kpi--orange"><div className="nx-kpi__label">Horário de ponta</div><div className="nx-kpi__value mono" style={{ fontSize: '1.4rem' }}>{data?.peakStart}—{data?.peakEnd}</div></div>
        <div className="nx-kpi nx-kpi--cyan"><div className="nx-kpi__label">Distribuidora</div><div className="nx-kpi__value" style={{ fontSize: '1.2rem' }}>{data?.distributor ?? 'Não informada'}</div><div className="nx-kpi__hint">Vigência: {data?.validFrom ?? '—'} até {data?.validTo ?? '—'}</div></div>
      </div>
      <div className="nx-card" style={{ marginTop: 'var(--nx-space-6)' }}>
        <h2 style={{ fontSize: '1rem' }}>Pico diário (últimos 30 dias)</h2>
        {!data?.peaksPerDay.length ? <p style={{ color: 'var(--nx-mute)' }}>Sem leituras.</p> : (
          <div style={{ display: 'grid', gap: 4 }}>
            {data.peaksPerDay.map((p) => {
              const over = data.contractedDemandKw > 0 && p.peakKw > data.contractedDemandKw;
              return (
                <div key={p.date} style={{ display: 'grid', gridTemplateColumns: '110px 1fr 100px', alignItems: 'center', gap: 12, fontSize: '.875rem' }}>
                  <div className="mono">{p.date}</div>
                  <div style={{ background: 'var(--nx-bg-alt)', borderRadius: 4, height: 20, position: 'relative' }}>
                    <div style={{ width: `${(p.peakKw / maxKw) * 100}%`, background: over ? 'var(--nx-danger)' : 'var(--nx-cyan)', height: '100%', borderRadius: 4 }} />
                  </div>
                  <div className="mono" style={{ color: over ? 'var(--nx-danger)' : 'var(--nx-ink)' }}>{p.peakKw.toFixed(1)} kW</div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// ---------- 14. Rel. de Consumos ----------
export function RelConsumoPage() {
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const q = useMemo(() => {
    const p = new URLSearchParams();
    if (from) p.set('from', new Date(from).toISOString());
    if (to) p.set('to', new Date(to).toISOString());
    return p.toString();
  }, [from, to]);
  const { data } = useApi<{ rows: Array<{ driver: string; idTag: string; transactions: number; kwh: number; costBrl: number }>; totalTransactions: number }>(`/api/financial/rel-consumo?${q}`, [q]);
  function csv() {
    const rows = data?.rows ?? [];
    const lines = ['motorista;idTag;transacoes;kwh;custo_brl'];
    for (const r of rows) lines.push(`${r.driver};${r.idTag};${r.transactions};${r.kwh};${r.costBrl}`);
    const blob = new Blob([lines.join('\r\n')], { type: 'text/csv;charset=utf-8' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `rel-consumo-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
  }
  // XLSX oficial: reaproveita o export de `sessions` cobrindo o mesmo período
  // — é a fonte primária das linhas agregadas nesta tela. O endpoint exige
  // `from` e `to`; enquanto o operador não escolhe, o botão fica desabilitado
  // com título explicativo.
  const xlsxHref = useMemo(() => {
    if (!from || !to) return null;
    const p = new URLSearchParams();
    p.set('from', new Date(from).toISOString());
    p.set('to', new Date(to).toISOString());
    return `/api/reports/xlsx/sessions?${p.toString()}`;
  }, [from, to]);
  const xlsxFilename = useMemo(() => {
    const f = from ? new Date(from).toISOString().slice(0, 10) : 'inicio';
    const t = to ? new Date(to).toISOString().slice(0, 10) : 'fim';
    return `nxvolt-rel-consumo-${f}_${t}.xlsx`;
  }, [from, to]);
  return (
    <div>
      <div className="nx-dash__head">
        <h1>Rel. de Consumos</h1>
        <div style={{ display: 'flex', gap: 'var(--nx-space-2)', flexWrap: 'wrap' }}>
          <button className="nx-btn nx-btn--ghost-dark" onClick={csv} disabled={!data?.rows.length}>⤓ CSV</button>
          <DownloadButton
            href={xlsxHref ?? '#'}
            filename={xlsxFilename}
            label="Exportar XLSX"
            variant="primary"
            disabled={!xlsxHref}
            title={
              xlsxHref
                ? 'Baixa as sessões do período em XLSX (mesma base do agregado desta tela)'
                : 'Selecione um período para exportar'
            }
          />
        </div>
      </div>
      <div className="nx-card" style={{ marginBottom: 'var(--nx-space-4)' }}>
        <div className="nx-filters">
          <label className="nx-field"><span>De</span><input type="datetime-local" value={from} onChange={(e) => setFrom(e.target.value)} /></label>
          <label className="nx-field"><span>Até</span><input type="datetime-local" value={to} onChange={(e) => setTo(e.target.value)} /></label>
        </div>
      </div>
      <div className="nx-card"><div className="nx-table-wrap">
        <table className="nx-table">
          <thead><tr><th>Motorista</th><th>idTag</th><th>Transações</th><th>Energia (kWh)</th><th>Custo (R$)</th></tr></thead>
          <tbody>
            {(data?.rows ?? []).map((r) => (
              <tr key={r.idTag}>
                <td>{r.driver}</td>
                <td className="mono">{r.idTag}</td>
                <td className="mono">{r.transactions}</td>
                <td className="mono">{r.kwh.toFixed(3)}</td>
                <td className="mono">R$ {r.costBrl.toFixed(2)}</td>
              </tr>
            ))}
            {!data?.rows.length && <tr><td colSpan={5} style={{ color: 'var(--nx-mute)', textAlign: 'center' }}>Sem transações.</td></tr>}
          </tbody>
        </table>
      </div></div>
    </div>
  );
}

// ---------- 15. Horário de Ponta ----------
export function HorarioPontaPage() {
  const { data } = useApi<{ peakStart: string; peakEnd: string; peakBrl: number; offPeakBrl: number; hourlyKwh: Array<{ hour: number; kwh: number }> }>('/api/financial/peak');
  const maxKwh = useMemo(() => Math.max(1, ...(data?.hourlyKwh ?? []).map((h) => h.kwh)), [data]);
  const inPeak = (h: number) => {
    if (!data) return false;
    const [ps] = data.peakStart.split(':').map(Number);
    const [pe] = data.peakEnd.split(':').map(Number);
    return ps! <= pe! ? h >= ps! && h < pe! : h >= ps! || h < pe!;
  };
  return (
    <div>
      <h1>Horário de Ponta</h1>
      <div className="nx-kpi-grid">
        <div className="nx-kpi nx-kpi--orange"><div className="nx-kpi__label">Janela de ponta</div><div className="nx-kpi__value mono" style={{ fontSize: '1.6rem' }}>{data?.peakStart}—{data?.peakEnd}</div></div>
        <div className="nx-kpi nx-kpi--navy"><div className="nx-kpi__label">Tarifa ponta</div><div className="nx-kpi__value mono">R$ {(data?.peakBrl ?? 0).toFixed(4)}/kWh</div></div>
        <div className="nx-kpi nx-kpi--success"><div className="nx-kpi__label">Tarifa fora de ponta</div><div className="nx-kpi__value mono">R$ {(data?.offPeakBrl ?? 0).toFixed(4)}/kWh</div></div>
      </div>
      <div className="nx-card" style={{ marginTop: 'var(--nx-space-6)' }}>
        <h2 style={{ fontSize: '1rem' }}>Distribuição de energia por hora do dia</h2>
        <div style={{ display: 'grid', gap: 4 }}>
          {(data?.hourlyKwh ?? []).map((h) => (
            <div key={h.hour} style={{ display: 'grid', gridTemplateColumns: '50px 1fr 100px', alignItems: 'center', gap: 12, fontSize: '.875rem' }}>
              <div className="mono">{String(h.hour).padStart(2, '0')}h</div>
              <div style={{ background: 'var(--nx-bg-alt)', borderRadius: 4, height: 16, position: 'relative' }}>
                <div style={{ width: `${(h.kwh / maxKwh) * 100}%`, background: inPeak(h.hour) ? 'var(--nx-orange)' : 'var(--nx-cyan)', height: '100%', borderRadius: 4 }} />
              </div>
              <div className="mono">{h.kwh.toFixed(2)} kWh</div>
            </div>
          ))}
        </div>
        <p style={{ color: 'var(--nx-mute)', fontSize: '.75rem', marginTop: 'var(--nx-space-3)' }}>Laranja = janela de ponta, ciano = fora de ponta.</p>
      </div>
    </div>
  );
}

// ---------- Sistema — Logs ----------
export function SystemLogsPage() {
  const { data } = useApi<{ logs: Array<{ id: number; userId: number | null; userEmail: string | null; action: string; targetKind: string | null; targetId: string | null; meta: string | null; ip: string | null; createdAt: string }> }>('/api/system-logs');
  const { user } = useAuth();
  if (user?.role !== 'admin') return <p>Sem permissão.</p>;
  return (
    <div>
      <h1>Logs do Sistema</h1>
      <div className="nx-card"><div className="nx-table-wrap">
        <table className="nx-table">
          <thead><tr><th>Quando</th><th>Usuário</th><th>Ação</th><th>Alvo</th><th>Meta</th><th>IP</th></tr></thead>
          <tbody>
            {(data?.logs ?? []).map((l) => (
              <tr key={l.id}>
                <td className="mono" style={{ fontSize: '.75rem' }}>{fmtDate(l.createdAt)}</td>
                <td className="mono">{l.userEmail ?? l.userId ?? '—'}</td>
                <td className="mono">{l.action}</td>
                <td className="mono">{l.targetKind}{l.targetId ? `#${l.targetId}` : ''}</td>
                <td className="mono" style={{ fontSize: '.7rem', maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis' }}>{l.meta}</td>
                <td className="mono">{l.ip ?? '—'}</td>
              </tr>
            ))}
            {!data?.logs.length && <tr><td colSpan={6} style={{ color: 'var(--nx-mute)', textAlign: 'center' }}>Sem eventos registrados.</td></tr>}
          </tbody>
        </table>
      </div></div>
    </div>
  );
}
